import { useState } from 'react';
import { api } from '../api.js';
import { toast } from './Toasts.jsx';
import { isStale as recIsStale, daysSilent, STALE_DAYS } from '@hunter/shared/applications';

// T256(12) — both Copy buttons called navigator.clipboard directly and said
// nothing either way, so a copy that silently failed (no permission, insecure
// origin, clipboard absent) looked exactly like one that worked. Mirrors the
// pattern already in InterviewPrepModal.
async function copy(text) {
  try {
    await navigator.clipboard.writeText(text);
    toast('Copied to clipboard', { kind: 'success' });
  } catch {
    toast('Copy failed — select the text and copy manually', { kind: 'error' });
  }
}

// T088 — Pipeline coach panels. Three inline panels that render in the
// applications side drawer based on the rec's stage:
//
//   FollowUpPanel       — appears when rec is applied/screening and the
//                         application is >= STALE_THRESHOLD_DAYS old
//   InterviewRetroPanel — appears when rec.status === 'interview'
//   NegotiationPrepPanel— appears when rec.status === 'offer'
//
// Each panel: collapsed CTA → generation → editable result → optional
// follow-up action (mark sent, etc.).

// T223 — the FOURTH copy of "no activity", now the shared one.
//
// This measured from `appliedAt` alone, so it never moved: an application that
// advanced applied → screening yesterday still offered a follow-up nudge, and
// one at `interview` offered none at all — `interview` was not in its list,
// even though an interview that has gone quiet is exactly when a nudge is
// worth sending.


// ─── Follow-up panel ───────────────────────────────────────────────────────

export function FollowUpPanel({ rec, onUpdated }) {
  const stale = recIsStale(rec);
  const existing = rec?.followUpEmail ?? null;
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState(existing?.subject ?? '');
  const [body, setBody] = useState(existing?.body ?? '');
  const [saving, setSaving] = useState(false);

  // Only render when stale or already has a draft.
  if (!stale && !existing) return null;

  const generate = async () => {
    setLoading(true);
    try {
      const r = await api.post(`/recommendations/${rec.id}/follow-up-email`, {});
      setSubject(r.followUpEmail.subject);
      setBody(r.followUpEmail.body);
      onUpdated?.({ followUpEmail: r.followUpEmail });
      toast('Follow-up email drafted', { kind: 'success' });
    } catch (e) {
      toast(e?.body?.message || e?.message || 'Draft failed', { kind: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/recommendations/${rec.id}/follow-up-email`, { subject, body });
      onUpdated?.({ followUpEmail: r.followUpEmail });
      setEditing(false);
      toast('Saved', { kind: 'success' });
    } catch (e) {
      toast(e?.body?.message || e?.message || 'Save failed', { kind: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const markSent = async (sent) => {
    try {
      const r = await api.patch(`/recommendations/${rec.id}/follow-up-email`, { markSent: sent });
      onUpdated?.({ followUpEmail: r.followUpEmail });
      toast(sent ? 'Marked sent' : 'Un-marked sent', { kind: 'success' });
    } catch (e) {
      toast(e?.body?.message || e?.message || 'Update failed', { kind: 'error' });
    }
  };

  // T254(2) — the panel is GATED on silence (recIsStale) but used to REPORT
  // age since applying. On a rec that replied on day 2 and went quiet, the two
  // numbers diverge, and the bigger one is the one it showed. Report the number
  // the gate actually used.
  const silent = daysSilent(rec);

  return (
    <div className="rounded-lg border border-amber-700 bg-amber-950/15 p-3">
      <div className="flex items-baseline justify-between">
        <p className="text-[10px] uppercase tracking-widest text-amber-400">
          ⏱ Stale application · {silent}d with no reply
        </p>
        {existing?.sentAt ? (
          <span className="rounded border border-emerald-700 px-1.5 py-0.5 text-[10px] text-emerald-300">sent</span>
        ) : null}
      </div>
      {!existing && !loading && (
        <>
          <p className="mt-1 text-xs text-slate-300">
            {STALE_DAYS}+ days without a reply — common time to send a polite check-in.
          </p>
          <button
            type="button"
            onClick={generate}
            className="mt-2 rounded-md border border-amber-700 px-3 py-1 text-xs text-amber-300 hover:bg-amber-900/30"
          >
            ✨ Draft follow-up email
          </button>
        </>
      )}
      {loading && (
        <p className="mt-2 text-sm text-amber-300">
          <span className="mr-2 inline-block h-2 w-2 animate-pulse rounded-full bg-amber-500" />
          Drafting…
        </p>
      )}
      {existing && !editing && (
        <div className="mt-2 space-y-2">
          <div>
            <p className="text-[10px] uppercase tracking-wide text-slate-500">Subject</p>
            <p className="text-sm text-slate-100">{existing.subject}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wide text-slate-500">Body</p>
            <p className="whitespace-pre-wrap text-sm text-slate-200">{existing.body}</p>
          </div>
          {existing.tips?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wide text-slate-500">Tips</p>
              <ul className="mt-1 space-y-0.5 text-[11px] text-slate-400">
                {existing.tips.map((t, i) => <li key={i}>• {t}</li>)}
              </ul>
            </div>
          )}
          <div className="flex flex-wrap gap-2 pt-1">
            <button type="button" onClick={() => copy(`Subject: ${existing.subject}\n\n${existing.body}`)} className="rounded border border-slate-700 px-2 py-0.5 text-[11px] text-slate-300 hover:border-brand-600">Copy</button>
            <button type="button" onClick={() => { setEditing(true); setSubject(existing.subject); setBody(existing.body); }} className="rounded border border-slate-700 px-2 py-0.5 text-[11px] text-slate-300 hover:border-brand-600">Edit</button>
            <button type="button" onClick={generate} className="rounded border border-slate-700 px-2 py-0.5 text-[11px] text-slate-300 hover:border-brand-600">Regenerate</button>
            {existing.sentAt ? (
              <button type="button" onClick={() => markSent(false)} className="rounded border border-slate-700 px-2 py-0.5 text-[11px] text-slate-400 hover:border-brand-600">Un-mark sent</button>
            ) : (
              <button type="button" onClick={() => markSent(true)} className="rounded border border-emerald-700 px-2 py-0.5 text-[11px] text-emerald-300 hover:bg-emerald-900/30">Mark sent ✓</button>
            )}
          </div>
        </div>
      )}
      {existing && editing && (
        <div className="mt-2 space-y-2">
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full rounded border border-slate-700 bg-slate-950/40 px-2 py-1 text-sm text-slate-100"
            placeholder="Subject"
          />
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={8}
            className="w-full rounded border border-slate-700 bg-slate-950/40 px-2 py-1 text-sm text-slate-100"
            placeholder="Body"
          />
          <div className="flex gap-2">
            <button type="button" onClick={saveEdit} disabled={saving} className="rounded-md bg-brand-600 px-3 py-1 text-xs font-medium text-white hover:bg-brand-500 disabled:opacity-50">{saving ? 'saving…' : 'Save'}</button>
            <button type="button" onClick={() => setEditing(false)} className="rounded-md border border-slate-700 px-3 py-1 text-xs text-slate-300 hover:border-brand-600">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Interview retrospective panel ─────────────────────────────────────────

export function InterviewRetroPanel({ rec, onUpdated }) {
  const existing = rec?.interviewRetro ?? null;
  const [notes, setNotes] = useState(existing?.interviewNotes ?? '');
  const [loading, setLoading] = useState(false);

  if (rec?.status !== 'interview') return null;

  const generate = async () => {
    if (notes.trim().length < 20) {
      toast('Add some notes from the interview first (at least 20 chars)', { kind: 'info' });
      return;
    }
    setLoading(true);
    try {
      const r = await api.post(`/recommendations/${rec.id}/interview-retro`, { interviewNotes: notes });
      onUpdated?.({ interviewRetro: r.interviewRetro });
      toast('Retro generated', { kind: 'success' });
    } catch (e) {
      toast(e?.body?.message || e?.message || 'Generation failed', { kind: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-lg border border-brand-700 bg-brand-600/5 p-3">
      <p className="text-[10px] uppercase tracking-widest text-brand-400">🎙 Interview retrospective</p>
      <div className="mt-2">
        <label className="text-[10px] uppercase tracking-wide text-slate-400">What was discussed in the interview?</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={4}
          placeholder='e.g. "They drilled into my Kubernetes networking work. CTO asked about cost trade-offs. Hiring manager hinted at a system-design round next."'
          className="mt-1 w-full rounded border border-slate-700 bg-slate-950/40 px-2 py-1 text-sm text-slate-100"
        />
      </div>
      <button
        type="button"
        onClick={generate}
        disabled={loading}
        className="mt-2 rounded-md bg-brand-600 px-3 py-1 text-xs font-medium text-white hover:bg-brand-500 disabled:opacity-50"
      >
        {loading ? 'generating…' : existing ? '↻ Regenerate' : '✨ Generate thank-you + follow-up prep'}
      </button>

      {existing && (
        <div className="mt-3 space-y-3">
          {existing.thankYouNote?.body && (
            <div>
              <p className="text-[10px] uppercase tracking-wide text-emerald-400">Thank-you note</p>
              <p className="mt-0.5 text-sm font-medium text-slate-100">{existing.thankYouNote.subject}</p>
              <p className="mt-1 whitespace-pre-wrap text-sm text-slate-200">{existing.thankYouNote.body}</p>
              <button
                type="button"
                onClick={() => copy(`Subject: ${existing.thankYouNote.subject}\n\n${existing.thankYouNote.body}`)}
                className="mt-1 rounded border border-slate-700 px-2 py-0.5 text-[11px] text-slate-300 hover:border-brand-600"
              >
                Copy
              </button>
            </div>
          )}
          {existing.likelyFollowUpQuestions?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wide text-amber-400">Likely follow-up questions</p>
              <ul className="mt-1 space-y-1.5">
                {existing.likelyFollowUpQuestions.map((q, i) => (
                  <li key={i} className="rounded border border-slate-800 bg-slate-950/40 p-2">
                    <p className="text-sm font-medium text-slate-100">{q.question}</p>
                    {q.why && <p className="mt-0.5 text-[11px] italic text-slate-500">{q.why}</p>}
                    {q.prepNote && <p className="mt-0.5 text-[11px] text-emerald-300">→ {q.prepNote}</p>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {existing.overallAssessment && (
            <div>
              <p className="text-[10px] uppercase tracking-wide text-slate-400">Assessment</p>
              <p className="mt-0.5 text-sm leading-relaxed text-slate-300">{existing.overallAssessment}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Negotiation prep panel ────────────────────────────────────────────────

export function NegotiationPrepPanel({ rec, onUpdated }) {
  const existing = rec?.negotiationPrep ?? null;
  const [offerNotes, setOfferNotes] = useState(existing?.offerNotes ?? '');
  const [loading, setLoading] = useState(false);

  if (rec?.status !== 'offer') return null;

  const generate = async () => {
    setLoading(true);
    try {
      const r = await api.post(`/recommendations/${rec.id}/negotiation-prep`, {
        offerNotes: offerNotes || undefined,
      });
      onUpdated?.({ negotiationPrep: r.negotiationPrep });
      toast('Negotiation prep generated', { kind: 'success' });
    } catch (e) {
      toast(e?.body?.message || e?.message || 'Generation failed', { kind: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fmtMoney = (n, ccy) =>
    Number.isFinite(n) && ccy ? `${ccy} ${Intl.NumberFormat().format(n)}` : '—';

  return (
    <div className="rounded-lg border border-emerald-700 bg-emerald-950/20 p-3">
      <p className="text-[10px] uppercase tracking-widest text-emerald-400">💰 Negotiation prep</p>
      <div className="mt-2">
        <label className="text-[10px] uppercase tracking-wide text-slate-400">Offer details (optional)</label>
        <textarea
          value={offerNotes}
          onChange={(e) => setOfferNotes(e.target.value)}
          rows={2}
          placeholder='e.g. "650k DKK base + 5% bonus + 4 weeks vacation"'
          className="mt-1 w-full rounded border border-slate-700 bg-slate-950/40 px-2 py-1 text-sm text-slate-100"
        />
      </div>
      <button
        type="button"
        onClick={generate}
        disabled={loading}
        className="mt-2 rounded-md bg-emerald-700 px-3 py-1 text-xs font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
      >
        {loading ? 'generating…' : existing ? '↻ Regenerate' : '✨ Generate negotiation prep'}
      </button>

      {existing && (
        <div className="mt-3 space-y-3">
          {existing.marketRange && (
            <div>
              <p className="text-[10px] uppercase tracking-wide text-emerald-400">
                Market range · confidence: {existing.marketRange.confidence}
              </p>
              <div className="mt-1 grid grid-cols-3 gap-2 text-sm">
                <div className="rounded border border-slate-800 bg-slate-950/40 p-2">
                  <p className="text-[10px] text-slate-500">low</p>
                  <p className="font-mono text-slate-100">{fmtMoney(existing.marketRange.lowAnnual, existing.marketRange.currency)}</p>
                </div>
                <div className="rounded border border-emerald-700 bg-emerald-950/30 p-2">
                  <p className="text-[10px] text-emerald-400">mid</p>
                  <p className="font-mono text-emerald-100">{fmtMoney(existing.marketRange.midAnnual, existing.marketRange.currency)}</p>
                </div>
                <div className="rounded border border-slate-800 bg-slate-950/40 p-2">
                  <p className="text-[10px] text-slate-500">high</p>
                  <p className="font-mono text-slate-100">{fmtMoney(existing.marketRange.highAnnual, existing.marketRange.currency)}</p>
                </div>
              </div>
              {existing.marketRange.rationale && (
                <p className="mt-1 text-[11px] italic text-slate-500">{existing.marketRange.rationale}</p>
              )}
            </div>
          )}

          {existing.angles?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wide text-emerald-400">Angles</p>
              <ul className="mt-1 space-y-1.5">
                {existing.angles.map((a, i) => (
                  <li key={i} className="rounded border border-slate-800 bg-slate-950/40 p-2">
                    <p className="text-xs font-medium text-emerald-300">{a.name}</p>
                    <p className="mt-1 text-sm italic leading-relaxed text-slate-100">&ldquo;{a.script}&rdquo;</p>
                    {a.when && <p className="mt-1 text-[11px] text-slate-500">{a.when}</p>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {existing.nonSalaryAsks?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wide text-emerald-400">Non-salary asks</p>
              <ul className="mt-1 flex flex-wrap gap-1.5">
                {existing.nonSalaryAsks.map((a, i) => (
                  <li key={i} className="rounded-full border border-slate-700 px-2 py-0.5 text-[11px] text-slate-300">
                    {a}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {existing.rationale && (
            <p className="rounded border border-slate-800 bg-slate-950/40 p-2 text-[11px] text-slate-300">
              <strong>Overall:</strong> {existing.rationale}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
